import { type QueryFunctionContext, useQuery } from "@tanstack/react-query";
import { type BasePerformanceRequestFilterParams, type PaginationParams } from "features/Library/Common/models";
import { type PerformanceFilter } from "features/Library/Common/utils/performanceUtils";
import { peopleReportingService } from "features/Reporting/services/peopleReportingService";
import {
  type PeopleActivityDetails,
  type PeopleInteractionItem,
  type PeopleOutreachItem,
} from "features/Reporting/types/people";
import { v4 } from "uuid";
import { type FetchPageResult } from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";

const countHeaderName = process.env.REACT_APP_COUNT_HEADER_NAME as string;

export const fetchPeopleInteractionTablePaginated = async (
  filter: PerformanceFilter,
  pagination?: PaginationParams,
  signal?: AbortSignal,
): Promise<FetchPageResult<PeopleInteractionItem>> => {
  const response = await peopleReportingService.activity.getPeopleInteractionTable(filter, pagination, signal);
  const rows = response.data.map((d) => ({ ...d, id: d.InteractionType })).filter((d) => d.InteractionCount > 0);

  return {
    rows,
    totalCount: Number.parseInt(response.headers[countHeaderName]),
  };
};

export const fetchPeopleOutreachTablePaginated = async (
  filter: PerformanceFilter,
  pagination?: PaginationParams,
  signal?: AbortSignal,
): Promise<FetchPageResult<PeopleOutreachItem>> => {
  const response = await peopleReportingService.activity.getPeopleOutreachTable(filter, pagination, signal);
  const rows = response.data.map((d) => ({ ...d, id: d.OutreachType })).filter((d) => d.OutreachCount > 0);

  return {
    rows,
    totalCount: Number.parseInt(response.headers[countHeaderName]),
  };
};

export type PeopleActivityDrilldownFilters = {
  dateFilter: BasePerformanceRequestFilterParams;
  selectedType: string;
  selectedCategory: string;
};

export const buildPeopleActivityDrilldownQueryKey = ({
  dateFilter,
  selectedType,
  selectedCategory,
}: PeopleActivityDrilldownFilters) => ["people activity details", dateFilter, selectedType, selectedCategory] as const;

export const fetchPeopleActivityDetailsPage = async (
  filters: PeopleActivityDrilldownFilters,
  pagination: PaginationParams,
  signal?: AbortSignal,
): Promise<FetchPageResult<PeopleActivityDetails>> => {
  const { dateFilter, selectedType, selectedCategory } = filters;

  const response =
    selectedCategory === "outreach"
      ? await peopleReportingService.activity.getPeopleOutreachDetails(
          { ...dateFilter, activityType: selectedType },
          pagination,
          signal,
        )
      : await peopleReportingService.activity.getPeopleInteractionDetails(
          { ...dateFilter, activityType: selectedType },
          pagination,
          signal,
        );

  const rows = response.data.map((d) => {
    return {
      ...d,
      // No values are guaranteed to be unique.
      id: v4(),
    };
  });

  return {
    rows,
    totalCount: Number.parseInt(response.headers[countHeaderName]),
  };
};

const getDetails = async ({
  queryKey,
  signal,
}: QueryFunctionContext<
  [_name: string, filters: BasePerformanceRequestFilterParams, selectedType: string, selectedCategory: string]
>) => {
  const pagination: PaginationParams = { skip: 0, top: 1 };
  const filters: PeopleActivityDrilldownFilters = {
    dateFilter: queryKey[1],
    selectedType: queryKey[2],
    selectedCategory: queryKey[3],
  };

  const result = await fetchPeopleActivityDetailsPage(filters, pagination, signal);
  return result.rows;
};

export const usePeopleOutreachTableQuery = (dateFilter: PerformanceFilter) =>
  useQuery({
    queryFn: ({ signal }) => fetchPeopleOutreachTablePaginated(dateFilter, undefined, signal),
    queryKey: ["people outreach table", dateFilter],
  });

export const usePeopleInteractionTableQuery = (dateFilter: PerformanceFilter) =>
  useQuery({
    queryFn: ({ signal }) => fetchPeopleInteractionTablePaginated(dateFilter, undefined, signal),
    queryKey: ["people interaction table", dateFilter],
  });
